/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Link } from '@repo/ui/i18n/navigation';
import { cn } from '@repo/ui/lib/utils';
import Image from 'next/image';
import NextLink from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import * as React from 'react';

import { AuthButton } from '../auth/auth-button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '../navigation-menu';
import { MobileMenu } from './mobile-menu';

export interface HeaderNavItem {
  label: string;
  href?: string;
  items?: {
    title: string;
    href: string;
    description?: string;
    icon?: string;
  }[];
  featured?: {
    title: string;
    href: string;
    description: string;
    image?: string;
  };
}

export interface HeaderProps {
  navItems?: HeaderNavItem[];
  callToAction?: {
    label: 'get_started';
    href: string;
  };
  className?: string;
}

const defaultNavItems: HeaderNavItem[] = [
  { label: 'pricing', href: '/#pricing' },
  { label: 'faqs', href: '/#faq' },
  { label: 'blog', href: '/blog' },
];

export const PatchLink = ({
  href,
  ...props
}: { href: string } & React.ComponentPropsWithoutRef<typeof NextLink>) => {
  const locale = useLocale();

  const localizedHref = href.startsWith('/') ? `/${locale}${href}` : href;

  return <NextLink href={localizedHref} {...props} />;
};

export function Header({ navItems, className }: HeaderProps) {
  const t = useTranslations('common');
  const completeNavItems = [...defaultNavItems, ...(navItems ?? [])];

  return (
    <header
      className={cn(
        'fixed top-4 left-1/2 -translate-x-1/2 z-50 w-fit',
        'bg-white/50 backdrop-blur-sm shadow-sm border border-gray-900/5',
        'rounded-full',
        className
      )}
    >
      {/* Visually hidden navigation for SEO and accessibility */}
      <nav aria-label='Main navigation' className='sr-only'>
        <ul>
          {completeNavItems.map((item) => (
            <li key={item.label}>
              {item.items ? (
                <ul>
                  {item.items.map((subItem) => (
                    <li key={subItem.title}>
                      <PatchLink href={subItem.href}>
                        {/* @ts-ignore */}
                        {t(subItem.title)}
                      </PatchLink>
                    </li>
                  ))}
                </ul>
              ) : (
                <PatchLink href={item.href ?? ''}>
                  {/* @ts-ignore */}
                  {t(item.label)}
                </PatchLink>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* End visually hidden navigation */}
      <div className='px-6 py-2'>
        <div className='flex h-12 items-center justify-between gap-8'>
          <Link href='/' className='w-8 h-8'>
            <Image
              src='/imagik-logo-small.webp'
              alt='Imagik'
              width={32}
              height={32}
            />
          </Link>

          <NavigationMenu className='hidden md:flex'>
            <NavigationMenuList className='gap-2 md:gap-4'>
              {completeNavItems.map((item) => (
                <NavigationMenuItem key={item.label}>
                  {item.items ? (
                    <>
                      <NavigationMenuTrigger>
                        {/* @ts-ignore */}
                        {t(item.label)}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <div className='grid w-[450px] gap-3 p-4 md:grid-cols-2'>
                          <div className='col-span-2 grid gap-3 relative'>
                            {item.items?.map((subItem) => (
                              <NavigationMenuLink asChild key={subItem.title}>
                                <PatchLink href={subItem.href}>
                                  <div className='flex items-center gap-3'>
                                    {subItem.icon && (
                                      <div className='h-10 w-10'>
                                        <Image
                                          src={`/icons/${subItem.icon}.svg`}
                                          className='shrink-0'
                                          alt={subItem.title}
                                          width={40}
                                          height={40}
                                        />
                                      </div>
                                    )}
                                    <div>
                                      <div className='text-sm font-medium leading-none'>
                                        {/* @ts-ignore */}
                                        {t(subItem.title)}
                                      </div>
                                      {subItem.description && (
                                        <p className='text-sm leading-snug text-muted-foreground whitespace-normal'>
                                          {/* @ts-ignore */}
                                          {t(subItem.description)}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </PatchLink>
                              </NavigationMenuLink>
                            ))}
                          </div>
                        </div>
                      </NavigationMenuContent>
                    </>
                  ) : (
                    <Link href={item.href ?? ''}>
                      <span className={navigationMenuTriggerStyle()}>
                        {/* @ts-ignore */}
                        {t(item.label)}
                      </span>
                    </Link>
                  )}
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
          <div className='flex items-center gap-4'>
            <AuthButton className='hidden md:flex' />
            <MobileMenu navItems={navItems ?? []} />
          </div>
        </div>
      </div>
    </header>
  );
}
